import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Icons,
  InputField,
  Modal,
  Tooltip,
  useToast,
} from "fds";
import "./index.scss";
import useLoginStore from "../../redux/dispatcher/useLoginStore";
import { useTranslation } from "react-i18next";
import utilsCommon from "../../shared/utils/common";
import { nanoid } from "@reduxjs/toolkit";
import useAdminLoginStore from "../../redux/dispatcher/useAdminLoginStore";
import { useNavigate } from "react-router-dom";

interface ChangePasswordModalProps {
  open: boolean;
  userId?: string;
  onClose: () => void;
  changeLoading: (status: boolean) => void;
}
const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  userId,
  onClose,
  changeLoading,
}) => {
  /** hook 정리 **/
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loginState, changePasswordFromMail } = useLoginStore();
  const { adminUpdateNewPassword } = useAdminLoginStore();
  const toasts = useToast();

  /** State 정리 **/
  const [err, changeErr] = useState("");
  const [password, changePassword] = useState({
    original: "",
    check: "",
  });
  const [isPwVisible, togglePWVisible] = useState({
    original: false,
    check: false,
  }); // 비밀번호 보임, 안보임 처리용
  const [initGoogleOTP, toogleInitGoolgeOTP] = useState(false);

  /** 함수 정리 **/
  /** 비밀번호 수정 클릭 */
  const handleUpdatePassword = async () => {
    /** 에러: 비밀번호가 유효하지 않음. */
    if (!utilsCommon.passwordValidation(password.original)) {
      changeErr("INVALID_PW_TYPE");
      return;
    }
    /** 에러: 새로운 비밀번호와 새로운 비밀번호 확인 값이 상이함 */
    if (password.original !== password.check) {
      changeErr("NOT_MATCH_WITH_ORIGINAL");
      return;
    }
    changeLoading(true);
    const resp: any = userId
      ? await adminUpdateNewPassword({
          userId: userId,
          password: password.original,
          initOtp: initGoogleOTP,
        })
      : await changePasswordFromMail(
          {
            uuid: loginState.actionInfo.uniqueId,
            users: [
              {
                userId: loginState.actionInfo.targetValue,
                password: password.original,
              },
            ],
          },
          initGoogleOTP
        );
    if (resp.payload) {
      changeLoading(false);
      window.location.href = `${process.env.PUBLIC_URL}/login?toastMsg=login.changePW.alert.success`;
      onClose();
    } else if (resp.error) {
      changeLoading(false);
      toasts.toastMsg(
        nanoid(),
        resp.error.message ?? t("login.changePW.alert.fail"),
        "error"
      );
      changeErr("");
    }
  };
  /** 비밀번호 수정 탭 - 비밀번호 에러 리소스 */
  const renderPWErrorStr = (type: string) => {
    switch (type) {
      case "INVALID_PW_TYPE":
        return t("login.login.error.passwordRegex");
      case "NOT_MATCH_WITH_ORIGINAL":
        return t("login.changePW.alert.notMatchPwMsg");
    }
    return undefined;
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("login.changePW.title")}
      width={540}
      body={
        <Box direction="column">
          <InputField
            placeholder=""
            size={"md"}
            label={t("login.login.userId")}
            value={loginState?.actionInfo?.targetValue ?? userId}
            disabled
          />
          <InputField
            placeholder={t("login.changePW.newPWPlaceholder")}
            maxLength={60}
            size={"md"}
            classes="mg mt-32"
            label={t("login.changePW.newPW")}
            value={password.original}
            onChange={(e) =>
              changePassword({ ...password, original: e.target.value })
            }
            type={isPwVisible.original ? "text" : "password"}
            trailingIcon={
              <Box alignItems={"center"}>
                <Tooltip
                  message={
                    isPwVisible.original
                      ? t("login.login.hidePW")
                      : t("login.login.showPW")
                  }
                >
                  <span
                    className={"pwViewBtn"}
                    onClick={() =>
                      togglePWVisible({
                        ...isPwVisible,
                        original: !isPwVisible.original,
                      })
                    }
                  >
                    <Icons
                      variant="line"
                      label="preview"
                      isOn={!isPwVisible.original}
                      stroke={"#717985"}
                    />
                  </span>
                </Tooltip>
              </Box>
            }
            isError={err === "INVALID_PW_TYPE"}
            helpMessage={err === "INVALID_PW_TYPE" ? renderPWErrorStr(err) : ""}
          />
          <InputField
            maxLength={60}
            label={t("login.changePW.checkPW")}
            size={"md"}
            classes="mg mt-32"
            placeholder={t("login.changePW.checkPWPlaceholder")}
            value={password.check}
            onChange={(e) =>
              changePassword({ ...password, check: e.target.value })
            }
            type={isPwVisible.check ? "text" : "password"}
            trailingIcon={
              <Box alignItems={"center"}>
                <Tooltip
                  message={
                    isPwVisible.check
                      ? t("login.login.hidePW")
                      : t("login.login.showPW")
                  }
                >
                  <span
                    className={"pwViewBtn"}
                    onClick={() =>
                      togglePWVisible({
                        ...isPwVisible,
                        check: !isPwVisible.check,
                      })
                    }
                  >
                    <Icons
                      variant="line"
                      label="preview"
                      isOn={!isPwVisible.check}
                      stroke={"#717985"}
                    />
                  </span>
                </Tooltip>
              </Box>
            }
            isError={err.includes("NOT_MATCH_WITH_ORIGINAL")}
            helpMessage={
              err === "NOT_MATCH_WITH_ORIGINAL" ? renderPWErrorStr(err) : ""
            }
          />
          <Box classes="mg mt-32" alignItems="center">
            {window.eco.serviceType !== "OCI" && (
              <Checkbox
                label={t("login.changePW.initGoogleOTP")}
                onClick={() => toogleInitGoolgeOTP(!initGoogleOTP)}
                check={initGoogleOTP}
              />
            )}
            <Tooltip
              message={t("login.changePW.googleOTPExaplain")}
              classes="mg ml-8"
            >
              <Icons variant="fill" label="questionmark" />
            </Tooltip>
          </Box>
        </Box>
      }
      footer={
        <Box>
          <Button
            btnStyle="fill"
            type="secondary"
            label={t("common.cancel")}
            onClick={onClose}
          />
          <Button
            btnStyle="fill"
            label={t("common.confirm")}
            onClick={handleUpdatePassword}
            state={
              password.check.length > 0 && password.original.length > 0
                ? "default"
                : "disable"
            }
          />
        </Box>
      }
    />
  );
};
export default ChangePasswordModal;
